<script setup lang="ts">import { computed as _computed } from 'vue';


interface SCardProps {
  /**
   * Card title
   */
  title?: string,
  /**
   * Card subtitle
   */
  subtitle?: string,
  /**
   * Card icon, displayed as large icon on the left of title and subtitle
   */
  icon?: string,
  /**
   * Whether the card is disabled. If true, all card content and actions are disabled
   */
  disabled?: boolean,
  /**
   * Whether the card is outlined or not
   */
  outlined?: boolean
  /**
   * Card size
   */
  size?: 'default' | 'small' | 'large',
  /**
   * Whether the card has padding or not
   */
  padding?: boolean
}
const props = defineProps({
  title: { default: '' },
  subtitle: { default: '' },
  icon: { default: '' },
  disabled: { type: Boolean, default: false },
  outlined: { type: Boolean, default: true },
  size: { default: 'default' },
  padding: { type: Boolean, default: true }
});

const hasTitleContent = _computed(() => !!props.title || !!props.subtitle || !!props.icon);

const titleClass = _computed(() => ({
  'body-1': props.size === 'default',
  'body-2': props.size === 'small',
  'text-h5': props.size === 'large',
}));

const subtitleClass = _computed(() => ({
  caption: props.size === 'default' || props.size === 'small',
  'body-2': props.size === 'large',
}));

const iconStyle = _computed(() => ({
  'margin-top': props.size === 'large' ? '4px' : '',
}));

</script>

<template lang="pug">
v-card.scard( v-bind="$attrs" :disabled="disabled" :outlined="outlined" :class="padding ? 'pa-4' : 'no-padding'" )
  .scard-header.d-flex.justify-space-between.align-center( v-if="hasTitleContent" :class="{'pb-4': $slots.default}" )
    .d-flex
      SIcon.mr-2( v-if="icon" :icon="icon" size="large" :style="iconStyle")
      div
        v-card-title.font-weight-medium.pa-0.ma-0.pr-2.textPrimary--text( v-if="title" :class="titleClass" ) {{ title }}
          slot( name="status")
        v-card-subtitle.ma-0.pa-0.pr-2.textSecondary--text( v-if="subtitle" :class="subtitleClass") {{ subtitle }}
          slot( name="link" )
    v-spacer
    slot( name="action" )
  v-card-text.scard-content.pa-0.textPrimary--text(v-if="$slots.default")
    slot

</template>
